import { useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LandingPage } from './pages/LandingPage';
import { LandingPicker } from './pages/LandingPicker';
import { LandingV1 } from './pages/LandingV1';
import { LandingV2 } from './pages/LandingV2';
import { LandingV3 } from './pages/LandingV3';
import { LandingV4 } from './pages/LandingV4';
import { LandingV5 } from './pages/LandingV5';
import { EditorPage } from './pages/EditorPage';
import { DashboardPage } from './pages/DashboardPage';
import { AccountPage } from './pages/AccountPage';
import { LibraryBrowser } from './pages/LibraryBrowserPage';
import { PairPage } from './pages/PairPage';
import { SetupPage } from './pages/SetupPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { EventWorkspacePage } from './pages/EventWorkspacePage';
import { InsightsPage } from './pages/InsightsPage';
import { SignInPage } from './pages/SignInPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { GoogleDriveCallbackPage } from './pages/GoogleDriveCallbackPage';
import { ConnectPage } from './pages/ConnectPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ConvexSetupRequired } from './components/ConvexSetupRequired';
import { isNextEventsExperience, NextEventsApp } from './next-events/NextEventsApp';
import { AuthKitProvider, useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';
import { ConvexReactClient, ConvexProviderWithAuth } from 'convex/react';

/**
 * Bridge hook: adapts WorkOS useAuth() to the shape Convex expects.
 */
function useConvexWorkOSAuth() {
  const { isLoading, user, getAccessToken } = useWorkOSAuth();

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      if (!user) return null;
      try {
        const token = await getAccessToken();
        return token ?? null;
      } catch {
        return null;
      }
    },
    [user, getAccessToken]
  );

  return {
    isLoading,
    isAuthenticated: !!user,
    fetchAccessToken,
  };
}

function AppRoutes({ hasConvex }: { hasConvex: boolean }) {
  if (isNextEventsExperience()) {
    return <NextEventsApp />;
  }

  if (!hasConvex) {
    return <ConvexSetupRequired />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingV1 />} />
      <Route path="/designs" element={<LandingPicker />} />
      <Route path="/original" element={<LandingPage />} />
      <Route path="/v1" element={<LandingV1 />} />
      <Route path="/v2" element={<LandingV2 />} />
      <Route path="/v3" element={<LandingV3 />} />
      <Route path="/v4" element={<LandingV4 />} />
      <Route path="/v5" element={<LandingV5 />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/library" element={<LibraryBrowser />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/pair" element={<PairPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/oauth/google-drive-callback" element={<GoogleDriveCallbackPage />} />
      <Route path="/connect" element={<ConnectPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/callback" element={<AuthCallbackPage />} />
      {/* WorkOS redirect routes — signup/invite/reset all go to sign-in (Google OAuth only) */}
      <Route path="/signup" element={<Navigate to="/signin" replace />} />
      <Route path="/signup/" element={<Navigate to="/signin" replace />} />
      <Route path="/invite" element={<Navigate to="/signin" replace />} />
      <Route path="/resetpassword" element={<Navigate to="/signin" replace />} />
      {/* /app redirects to dashboard — events-centric home screen */}
      <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/app/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/app/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/app/editor" element={<ProtectedRoute><EditorPage /></ProtectedRoute>} />
      <Route path="/app/event/:eventId" element={<ProtectedRoute><EventWorkspacePage /></ProtectedRoute>} />
      <Route path="/app/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

type AppProps = {
  client: ConvexReactClient | null;
};

/** Always use the canonical (non-www) origin for WorkOS redirect URIs. */
function getCanonicalOrigin(): string {
  const origin = window.location.origin;
  return origin.replace('://www.', '://');
}

function getSafeAuthReturnTo(state: unknown): string | null {
  if (!state || typeof state !== 'object' || !('returnTo' in state)) return null;
  const returnTo = (state as { returnTo?: unknown }).returnTo;
  if (typeof returnTo !== 'string') return null;
  if (!returnTo.startsWith('/') || returnTo.startsWith('//')) return null;
  return returnTo;
}

function App({ client }: AppProps) {
  const routes = (
    <BrowserRouter>
      <AppRoutes hasConvex={Boolean(client)} />
    </BrowserRouter>
  );

  return (
    <ErrorBoundary>
      <AuthKitProvider
        clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
        redirectUri={`${getCanonicalOrigin()}/callback`}
        onRedirectCallback={({ state }) => {
          const returnTo = getSafeAuthReturnTo(state);
          if (returnTo) window.location.replace(returnTo);
        }}
      >
        {client ? (
          <ConvexProviderWithAuth client={client} useAuth={useConvexWorkOSAuth}>
            {routes}
          </ConvexProviderWithAuth>
        ) : routes}
      </AuthKitProvider>
    </ErrorBoundary>
  );
}

export default App;
